import React, { Suspense, useEffect, useMemo } from "react";
import { jssPreset, StylesProvider } from "@material-ui/core/styles";
import { create } from "jss";
import rtl from "jss-rtl";
import { JobExtentList, AreasList } from "./config/jobsConfigData";
import { createMuiTheme, ThemeProvider } from "@material-ui/core/styles";
import { Route, Switch, BrowserRouter as Router } from "react-router-dom";
import Login from "./Screens/Login/Login";
import Jobs from "./Screens/Jobs/Jobs";
import Candidates from "./Screens/Candidates/Candidates";
import { heIL } from "@material-ui/core/locale";
import axios from "axios";
import "./App.css";
import WithMenuContainer from "./hoc/WithMenuContainer";
import ConfigurationListsContext from "./context/configurationListsContext";
import CandidateLogin from "./Screens/ProfilesManagement/CandidateProfile/Login/Login";
import CssBaseline from "@material-ui/core/CssBaseline";

import cookies from "js-cookie";
import LibiExplore from "./Screens/Explore/LibiExplore";
import { pink } from "@material-ui/core/colors";
import ErrorBoundary from "./components/ErrorBoundary/ErrorBoundary";
import AxiosErrorHandling from "./components/AxiosErrorHandling/AxiosErrorHandling";
import { ReactQueryDevtools } from "react-query/devtools";
import useUsers from "./customHooks/useUsers";
import useCompanies from "./customHooks/useCompanies";
import useProfessions from "./customHooks/useProfessionals";

const HLDashboard = React.lazy(() =>
  import("./Screens/Dashboard/HighLevelStatsDashboard"),
);

const Settings = React.lazy(() =>
  import("./Screens/Settings/MainSettings/Settings.js"),
);

const DeatiledActivityReport = React.lazy(() =>
  import("./Screens/Dashboard/DeatiledActivityReport"),
);

const DetailedDistributionReport = React.lazy(() =>
  import(
    "./Screens/Jobs/JobsList/JobDashboard/DetailedDistributionReport/DetailedDistributionReport"
  ),
);

const TracerCandidate = React.lazy(() =>
  import("./Screens/Tracers/TracerCandidate.js"),
);

const Tracers = React.lazy(() => import("./Screens/Tracers/Tracers.js"));

const PublishJobs = React.lazy(() => import("./publishJobs/PublishJobs.js"));

const TagsManager = React.lazy(() =>
  import("./Screens/TagsManager/TagsManager.js"),
);

const UploadTags = React.lazy(() =>
  import("./Screens/TagsManager/uploadTags.js"),
);

const Unsubscribe = React.lazy(() =>
  import("./components/Unsubscribe/Unsubscribe.js"),
);

const CandidateDashboard = React.lazy(() =>
  import(
    "./Screens/ProfilesManagement/CandidateProfile/Dashboard/CandidateDashboard"
  ),
);

const Registration = React.lazy(() =>
  import(
    "./Screens/ProfilesManagement/CandidateProfile/Registration/Registration"
  ),
);

const AIRererralsManager = React.lazy(() =>
  import("./Screens/AiReferrals/AIReferralsManager.jsx"),
);

const MissingJobsReport = React.lazy(() =>
  import("./components/MissingJobsReport/MissingJobsReport.jsx"),
);
const CustomerOnboardingReport = React.lazy(() =>
  import("./components/CustomerOnboardingReport/CustomerOnboardingReport.jsx"),
);
const CustomerInterview = React.lazy(() =>
  import("./components/CustomerInterview/CustomerInterview.jsx"),
);
const CampaignManager = React.lazy(() =>
  import("./components/CampaignManager/CampaignManager.jsx"),
);
const OnboardingJobStatus = React.lazy(() =>
  import("./components/OnboardingJobStatus/OnboardingJobStatus.jsx"),
);
const PublicationSso = React.lazy(() => import("./Screens/Sso/PublicationSso"));

//axios.defaults.baseURL = "http://localhost/libi-smart-project/backend";
axios.defaults.baseURL = "https://l-b.backoffice.libi.ai/";
//axios.defaults.baseURL = "https://testing-backoffice.libi.ai/";

axios.defaults.timeout = 80000;
axios.defaults.headers["Content-Type"] = "application/json; charset=UTF-8";

const jss = create({ plugins: [...jssPreset().plugins, rtl()] });

axios.interceptors.request.use(
  (request) => {
    if (
      request.url !== "/wp-json/jwt-auth/v1/token" &&
      !request.url.includes("/already_answered/") &&
      !request.url.includes("/candidateAuthentication/") &&
      !request.url.includes("/api/v2/job/") &&
      !request.url.includes("/api/v2/questionier_answers/") &&
      !request.url.includes("wp-json/api/v2/declinedJob/") &&
      !request.url.includes("/api/v2/unsubscribe") &&
      !request.url.includes("/api/v2/locateCandidate/") &&
      !request.url.includes("/api/v2/customer-interview/") &&
      !request.url.includes("/api/v2/onboarding-job-answer/") &&
      !request.url.includes("/api/v2/generate_login_passcode") &&
      !request.url.includes("/api/v2/generate_login_token") &&
      !request.url.includes("/api/v2/accept_registration") &&
      !request.url.includes("/api/v2/areas_professions_list") &&
      !request.url.includes("/api/v2/trace/") &&
      !request.url.includes("/api/v2/upload_trace_cv") &&
      !request.url.includes("/api/v2/send_trace") &&
      !request.url.includes("/api/v2/reset_password")
    ) {
      //check if this a candidate domain. all candidate domains must include /my-profile/lobby

      if (
        request.url.includes("/my-profile/lobby") ||
        request.url.includes("/my-profile/settings")
      ) {
        request.headers["Authorization"] = `Bearer ${cookies.get("token")}`;
      } else {
        request.headers["Authorization"] = `Bearer ${localStorage.getItem(
          "token",
        )}`;
      }
    }

    if (process.env.NODE_ENV === "development") console.log("request", request);
    return request;
  },
  (error) => {
    if (process.env.NODE_ENV === "development")
      console.log("request error", error);
    return Promise.reject(error);
  },
);
axios.interceptors.response.use(
  (response) => {
    if (process.env.NODE_ENV === "development")
      console.log("response", response);
    return response;
  },
  (error) => {
    if (process.env.NODE_ENV === "development")
      console.log("response error", error.response, error);

    if (
      (error.response && error.response.data.code === "jwt_auth_bad_iss") ||
      error.response.data.code === "jwt_auth_invalid_token" ||
      error.response.data.data.status === 401
    ) {
      localStorage.clear();
      //window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

const theme = createMuiTheme(
  {
    typography: {
      fontFamily: "Rubik, sans-serif",
      h4: {
        fontWeight: 700,
      },
      subtitle1: {
        fontWeight: 500,
        fontSize: "1.2rem",
      },
      body2: {
        fontSize: "1.1rem",
        fontWeight: 400,
      },
      smallText: {
        fontSize: "0.75rem",
        fontWeight: 400,
      },
    },
    palette: {
      primary: {
        main: "#FF0A90",
        light: "#FFA3D5",
        dark: "#A6005B",
      },
      secondary: {
        main: "#2941FF",
        light: "#8593FF",
        dark: "#001194",
      },
      success: {
        main: "#0BD03B",
        light: "#EDF9F0",
        dark: "#06681E",
      },
      text: {
        primary: "#363740",
        secondary: "#636575",
      },
      background: {
        default: "#F5F6F8",
      },
    },
    overrides: {
      MuiFilledInput: {
        root: {
          backgroundColor: "#F5F6F8",
        },
      },
      MuiInputLabel: {
        root: {
          color: "#A4A6B3",
        },
      },
      MuiChip: {
        root: {
          backgroundColor: pink[50],
          color: "#FF0A90",
          "& .MuiSvgIcon-root": {
            color: "#FF0A90",
          },
          size: "medium",
        },
      },
    },
    spacing: (factor) => `${0.5 * factor}rem`,
  },
  { direction: "rtl" },
  heIL,
);

function App() {
  const [professionsList, setProfessionalList] = React.useState([]);
  const [customersList, setCustomerList] = React.useState([]);
  const [isDarkMode, setIsDarkMode] = React.useState(false);
  const [users, setUsers] = React.useState([]);
  const [error, setError] = React.useState(null);
  const { data: usersData } = useUsers();
  const { data: companiesData } = useCompanies();
  const { data: professionsData } = useProfessions();

  const isConfigRoute =
    !window.location.pathname.includes("/login") &&
    !window.location.pathname.includes("/tracer/") &&
    !window.location.pathname.includes("/customer-interview/") &&
    !window.location.pathname.includes("/onboarding-job/") &&
    !window.location.pathname.includes("/publishJob") &&
    !window.location.pathname.includes("/publishjob/");

  const areasList = useMemo(
    () =>
      isConfigRoute
        ? Object.keys(AreasList).map((item) => ({
            id: item,
            value: AreasList[item],
          }))
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const jobExtentList = useMemo(
    () =>
      isConfigRoute
        ? Object.keys(JobExtentList).map((item) => ({
            id: item,
            value: JobExtentList[item],
          }))
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  React.useEffect(() => {
    if (window.location.pathname.includes("/my-profile/")) {
      if (window.location.pathname.includes("/my-profile/lobby")) {
        const token = cookies.get("token");
        if (!token) window.location.href = "/my-profile/login";
      }
    }
  }, []);

  useEffect(() => {
    setProfessionalList(professionsData);
    setCustomerList(companiesData);
    setUsers(usersData);
  }, [professionsData, usersData, companiesData]);

  const createCustomTheme = () => {
    return !isDarkMode
      ? createMuiTheme(
          {
            typography: {
              h2: {
                fontSize: "3rem",
                [theme.breakpoints.down("md")]: {
                  fontSize: "2rem",
                },
              },
              h3: {
                fontSize: "2rem",
              },
              h4: {
                fontSize: "1.5rem",
              },
            },
            spacing: (factor) => `${0.5 * factor}rem`,
            palette: {
              type: "light",
              text: {
                primary: "#363740",
              },
            },
          },
          { direction: "rtl" },
          heIL,
        )
      : createMuiTheme(
          {
            typography: {
              h2: {
                fontSize: "3rem",
              },
              h3: {
                fontSize: "2rem",
              },
              h4: {
                fontSize: "1.5rem",
              },
            },
            spacing: (factor) => `${0.5 * factor}rem`,
            palette: {
              type: "dark",
              background: {
                paper: "#212936",
                default: "#2B3648",
              },
            },
          },
          { direction: "rtl" },
          heIL,
        );
  };

  const handleToggleDarkMode = () => {
    setIsDarkMode((prev) => !prev);
  };

  const configContextValue = useMemo(
    () => ({ professionsList, customersList, jobExtentList, areasList, users }),
    [professionsList, customersList, jobExtentList, areasList, users],
  );

  return (
    <React.Fragment>
      <ConfigurationListsContext.Provider value={configContextValue}>
        <Router>
          <Switch>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <StylesProvider jss={jss}>
                <Switch>
                  {/* Public routes — no sidebar */}
                  <Route exact path="/login">
                    <Login />
                  </Route>
                  <Route exact path="/tracer/:traceid">
                    <Suspense fallback={<div>Loading...</div>}>
                      <TracerCandidate />
                    </Suspense>
                  </Route>
                  <Route path="/publishJob/:id" exact>
                    <Suspense fallback={<div>Loading...</div>}>
                      <PublishJobs />
                    </Suspense>
                  </Route>
                  <Route path="/unsubscribe/:auth" exact>
                    <Suspense fallback={<div>Loading...</div>}>
                      <Unsubscribe />
                    </Suspense>
                  </Route>
                  <Route path="/customer-interview/:token" exact>
                    <Suspense fallback={<div>Loading...</div>}>
                      <CustomerInterview />
                    </Suspense>
                  </Route>
                  <Route path="/onboarding-job/:token/:answer" exact>
                    <Suspense fallback={<div>Loading...</div>}>
                      <OnboardingJobStatus />
                    </Suspense>
                  </Route>
                  {/* Opened in a new tab by the "מערכת הפרסום" menu item;
                      checks the recruiter token itself. */}
                  <Route path="/sso/publication" exact>
                    <Suspense fallback={<div>Loading...</div>}>
                      <PublicationSso />
                    </Suspense>
                  </Route>

                  {/* Candidate routes (/my-profile/*) are handled by the
                      dedicated candidate <Switch> below. Exclude them here so
                      the admin catch-all <Route> doesn't match them and mount
                      WithMenuContainer, which would redirect candidates (who
                      authenticate via cookie, not localStorage) to /login. */}
                  <Route path="/my-profile" />

                  {/* Admin routes — single WithMenuContainer instance */}
                  <Route>
                    <WithMenuContainer>
                      <Switch>
                        <Route exact path="/dashboard">
                          <Suspense fallback={<div>Loading Data...</div>}>
                            <HLDashboard />
                          </Suspense>
                        </Route>
                        <Route exact path="/activityReport">
                          <Suspense fallback={<div>Loading...</div>}>
                            <DeatiledActivityReport />
                          </Suspense>
                        </Route>
                        <Route exact path="/missingJobsReport">
                          <Suspense fallback={<div>Loading...</div>}>
                            <MissingJobsReport />
                          </Suspense>
                        </Route>
                        <Route exact path="/customerOnboarding">
                          <Suspense fallback={<div>Loading...</div>}>
                            <CustomerOnboardingReport />
                          </Suspense>
                        </Route>
                        <Route exact path="/campaigns">
                          <Suspense fallback={<div>Loading...</div>}>
                            <CampaignManager />
                          </Suspense>
                        </Route>
                        <Route exact path="/settings">
                          <Suspense fallback={<div>Loading...</div>}>
                            <Settings />
                          </Suspense>
                        </Route>
                        <Route exact path="/explore">
                          <LibiExplore />
                        </Route>
                        <Route exact path="/candidates">
                          <Candidates />
                        </Route>
                        <Route
                          exact
                          path="/jobs/dashboard/:jobid/distribution/:distributionId"
                        >
                          <ErrorBoundary>
                            <Suspense fallback={<div>Loading...</div>}>
                              <DetailedDistributionReport />
                            </Suspense>
                          </ErrorBoundary>
                        </Route>
                        <Route exact path="/jobs/dashboard/:jobid">
                          <ErrorBoundary>
                            <Jobs innerpath="dashboard" />
                          </ErrorBoundary>
                        </Route>
                        <Route exact path="/jobs/addNew">
                          <Jobs innerpath="AddNewJob" />
                        </Route>
                        <Route exact path="/jobs/drafts">
                          <Jobs innerpath="drafts" />
                        </Route>
                        <Route exact path="/jobs/uploads/:jobid">
                          <Jobs innerpath="uploads" />
                        </Route>
                        <Route exact path="/jobs">
                          <Jobs innerpath="jobs" />
                        </Route>
                        <Route exact path="/tracers">
                          <Suspense fallback={<div>Loading...</div>}>
                            <Tracers />
                          </Suspense>
                        </Route>
                        <Route exact path="/jobReferrals">
                          <Suspense fallback={<div>Loading...</div>}>
                            <AIRererralsManager />
                          </Suspense>
                        </Route>
                        <Route exact path="/tags-manager/upload">
                          <Suspense fallback={<div>Loading...</div>}>
                            <UploadTags />
                          </Suspense>
                        </Route>
                        <Route exact path="/tags-manager">
                          <Suspense
                            fallback={<div>Loading Tag Manager...</div>}
                          >
                            <TagsManager />
                          </Suspense>
                        </Route>
                        <Route path="/">
                          <Jobs innerpath="jobs" />
                        </Route>
                      </Switch>
                    </WithMenuContainer>
                  </Route>
                </Switch>
              </StylesProvider>
            </ThemeProvider>
          </Switch>
          <Switch>
            <ThemeProvider theme={createCustomTheme()}>
              <CssBaseline />
              <StylesProvider jss={jss}>
                <Route exact path="/my-profile/login">
                  <CandidateLogin />
                </Route>
                <Route exact path="/my-profile/lobby">
                  <Suspense fallback={<div>Loading...</div>}>
                    <CandidateDashboard
                      onToggleDark={handleToggleDarkMode}
                      isDarkMode={isDarkMode}
                    />
                  </Suspense>
                </Route>
                <Route exact path="/my-profile/settings">
                  <Suspense fallback={<div>Loading...</div>}>
                    <CandidateDashboard
                      onToggleDark={handleToggleDarkMode}
                      isDarkMode={isDarkMode}
                    />
                  </Suspense>
                </Route>
                <Route exact path="/my-profile/signup">
                  <Suspense fallback={<div>Loading...</div>}>
                    <Registration />
                  </Suspense>
                </Route>
              </StylesProvider>
            </ThemeProvider>
          </Switch>
        </Router>
      </ConfigurationListsContext.Provider>
      {error && (
        <AxiosErrorHandling title="Catastrophic Failure" error={error} />
      )}
      <ReactQueryDevtools />
    </React.Fragment>
  );
}

export default App;
